import { Box, Button, CircularProgress } from "@mui/material"
import { useNavigate } from "react-router"
import { useEffect } from "react"
import { useLazyGetProductFamiliesQuery } from "@store/services/baseGraphFamiliesService"
import { Table, TableState, defaultTableState, tableStateSuffix } from "@components/Table"
import { getProductFamiliesColumns } from "@features/administration/products/EditProduct/const"
import { useLocalStorage } from "usehooks-ts"
import { GraphsTable } from "@features/administration/products/EditProduct/GraphsTable"

type FamiliesTableProps = {
  productKey: string
  organisationKey: string
  divisionKey: string
}

export default function FamiliesTable({
  productKey,
  organisationKey,
  divisionKey,
}: FamiliesTableProps) {
  const navigate = useNavigate()

  const [fetchFamilies, { data: productFamilies = [], isFetching }] =
    useLazyGetProductFamiliesQuery()
  const [tableState, setTableState] = useLocalStorage<TableState>(
    `families-table${tableStateSuffix}`,
    defaultTableState,
  )

  useEffect(() => {
    if (productKey && organisationKey && divisionKey) {
      fetchFamilies({
        organisationKey,
        divisionKey,
        productKey,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [productKey, organisationKey, divisionKey])

  const columns = getProductFamiliesColumns()

  const handleCollapseAll = () => {
    setTableState((previousState) => ({
      ...previousState,
      expanded: {},
    }))
  }

  return (
    <Box className={"families-table-container"}>
      {isFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table
          sessionStorageName={"families-table"}
          data={productFamilies}
          columns={columns}
          enableExpandAll={false}
          getRowId={(row) => row.key}
          renderDetailPanel={({ row }) => {
            return <GraphsTable productFamily={row.original} />
          }}
          muiTableBodyCellProps={({ row }) => {
            const onClick = () => {
              setTableState((previousState: TableState) => {
                const expanded =
                  previousState.expanded && typeof previousState.expanded === "object"
                    ? { ...previousState.expanded }
                    : {}
                if (row.id in expanded) {
                  delete expanded[row.id]
                } else {
                  expanded[row.id] = true
                }
                return {
                  ...previousState,
                  expanded,
                }
              })
            }
            return { onClick }
          }}
          muiTablePaperProps={{ sx: { overflow: "scroll" } }}
          renderTopToolbarCustomActions={() => (
            <Box sx={{ display: "inline" }}>
              <Button
                variant="contained"
                disabled={Object.keys(tableState.expanded).length === 0}
                onClick={handleCollapseAll}
                sx={{ ml: 1 }}
              >
                Collapse All
              </Button>
            </Box>
          )}
        />
      )}
    </Box>
  )
}
